import { CommonModule } from '@angular/common'
import {
  Component,
  inject,
  Input,
  OnChanges,
  SimpleChanges,
} from '@angular/core'
import { Router } from '@angular/router'
import { forkJoin } from 'rxjs'

import { Enums, HuntPackageRecord } from '@cyborg-monorepo/hunter/common'
import {
  BadgeComponent,
  CodeBlockComponent,
  EllipsisComponent,
  LinkComponent,
  MarkdownComponent,
  ThemeService,
  TypographyComponent,
} from '@hyperkit'

import { SearchUrlService } from '../../../research/services/search-url.service'
import { HuntPackageService } from '../../services/hunt-package.service'

type AnalystNote = NonNullable<
  HuntPackageRecord['running_analyst_notes']
>[number]

const TRIMMABLE_FIELDS = [
  'current_analyst_note',
  'analyst_note_summary',
  'analyst_note_attribution',
  'analyst_note_pivots',
] as const

@Component({
  standalone: true,
  selector: 'shared-analyst-notes',
  imports: [
    CommonModule,
    BadgeComponent,
    CodeBlockComponent,
    EllipsisComponent,
    LinkComponent,
    MarkdownComponent,
    TypographyComponent,
  ],
  templateUrl: './analyst-notes.component.html',
  styleUrls: ['./analyst-notes.component.scss'],
})
export class AnalystNotesComponent implements OnChanges {
  @Input() runningNotes: HuntPackageRecord['running_analyst_notes']
  @Input() threatDescription = ''

  private readonly huntPackageService = inject(HuntPackageService)
  private readonly searchUrlService = inject(SearchUrlService)
  private readonly router = inject(Router)
  private readonly themeService = inject(ThemeService)

  readonly theme = this.themeService.theme
  readonly huntPackageNameMap = new Map<string, string>()
  trimmedNotes: AnalystNote[] | undefined

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['runningNotes']) {
      this.trimmedNotes = this.runningNotes?.map((note) => ({
        ...note,
        ...Object.fromEntries(
          TRIMMABLE_FIELDS.map((field) => [
            field,
            note[field] ? this.trimWhitespace(note[field]) : note[field],
          ]),
        ),
      }))
      this.fetchAttributionTitles()
    }
  }

  isIntelAuthor(author?: Enums.AnalystNoteAuthorEnum): boolean {
    return (
      author === Enums.AnalystNoteAuthorEnum.TITAN_INTEL ||
      author === Enums.AnalystNoteAuthorEnum.VERITY_INTEL
    )
  }

  getHuntPackageNameByUUID(uuid: string): string {
    return this.huntPackageNameMap.get(uuid) ?? uuid
  }

  getThreatNameSearchHref(threatName: string): string {
    const link = this.searchUrlService.stateToRouterLink({
      filters: { threat_names: [threatName] },
      library: [
        Enums.CyborgIndexEnum.CYBORG_THREAT_PROFILES,
        Enums.CyborgIndexEnum.CYBORG_USE_CASES,
        Enums.CyborgIndexEnum.CYBORG_COLLECTIONS,
      ],
    })
    const urlTree = this.router.createUrlTree(link.routerLink as string[], {
      queryParams: link.queryParams,
    })
    return this.router.serializeUrl(urlTree)
  }

  getAdversaryNameSearchHref(adversaryName: string): string {
    const link = this.searchUrlService.stateToRouterLink({
      term: [adversaryName],
    })
    const urlTree = this.router.createUrlTree(link.routerLink as string[], {
      queryParams: link.queryParams,
    })
    return this.router.serializeUrl(urlTree)
  }

  private fetchAttributionTitles(): void {
    const uuids = [
      ...new Set(
        this.trimmedNotes
          ?.flatMap((note) => note.huntpackage_attribution ?? [])
          .filter(Boolean),
      ),
    ]

    if (uuids.length === 0) return

    forkJoin(
      uuids.map((uuid) => this.huntPackageService.getHuntPackageByUUID(uuid)),
    ).subscribe((packages: HuntPackageRecord[]) => {
      packages.forEach((pkg, i) => {
        this.huntPackageNameMap.set(uuids[i], pkg.title)
      })
    })
  }

  private trimWhitespace(value: string): string {
    return value
      .replace(/^(?:\s*(?:<\/?br\s*\/?>|\r?\n))+/gi, '')
      .trimStart()
      .replace(/(?:\s*(?:<\/?br\s*\/?>|\r?\n))+$/gi, '')
      .trimEnd()
  }
}
